import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Sparkles, Timer } from 'lucide-react';

interface GoYearlyOfferBannerProps {
  onUpgrade: () => Promise<void>;
  loading?: boolean;
  error?: string | null;
  onSeePlans: () => void;
}

const OFFER_DURATION_MS = 10 * 60 * 1000;
const OFFER_STORAGE_KEY = 'omniscribe-go59-offer-deadline';

function getOfferDeadline(): number {
  const now = Date.now();
  const saved = Number(window.localStorage.getItem(OFFER_STORAGE_KEY));
  if (Number.isFinite(saved) && saved > now && saved <= now + OFFER_DURATION_MS) {
    return saved;
  }

  const deadline = now + OFFER_DURATION_MS;
  window.localStorage.setItem(OFFER_STORAGE_KEY, String(deadline));
  return deadline;
}

function formatTimeUnit(value: number): string {
  return value.toString().padStart(2, '0');
}

const GoYearlyOfferBanner: React.FC<GoYearlyOfferBannerProps> = ({
  onUpgrade,
  loading = false,
  error,
  onSeePlans,
}) => {
  const [deadline, setDeadline] = useState(getOfferDeadline);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30);
    return () => window.clearInterval(timer);
  }, []);

  const remaining = Math.max(0, deadline - now);
  useEffect(() => {
    if (remaining > 0) return;

    const nextDeadline = Date.now() + OFFER_DURATION_MS;
    window.localStorage.setItem(OFFER_STORAGE_KEY, String(nextDeadline));
    setDeadline(nextDeadline);
    setNow(Date.now());
  }, [remaining]);

  const countdown = useMemo(() => {
    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const centiseconds = Math.floor((remaining % 1000) / 10);

    return {
      minutes: formatTimeUnit(minutes),
      seconds: formatTimeUnit(seconds),
      milliseconds: formatTimeUnit(centiseconds),
    };
  }, [remaining]);

  return (
    <section
      data-testid="go-yearly-offer-banner"
      className="relative overflow-hidden rounded-xl border border-indigo-100 bg-white p-5 shadow-lg shadow-slate-200/60 dark:border-indigo-500/20 dark:bg-zinc-900 dark:shadow-black/20 sm:p-6"
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-indigo-100 dark:bg-indigo-500/15" />
      <div className="pointer-events-none absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-slate-50 dark:bg-zinc-800/60" />

      <div className="relative grid gap-6 lg:grid-cols-[1fr,300px] lg:items-center">
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold tracking-normal text-slate-950 dark:text-white">
              Discounted Yearly Go Plan
            </h2>
            <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
              Just $5.00 per month
            </span>
            <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
              50% OFF
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <span className="text-5xl font-normal tracking-normal text-slate-950 dark:text-white">$59.00</span>
            <span className="pb-2 text-lg font-semibold text-slate-500 line-through dark:text-zinc-500">$120.00</span>
            <span className="pb-2 text-sm font-medium text-slate-500 dark:text-zinc-400">billed yearly</span>
          </div>

          <div className="grid gap-x-10 gap-y-3 text-sm text-slate-600 dark:text-zinc-300 sm:grid-cols-2">
            {['1,200 min/mo', 'Premium model', 'Speaker identification', 'Priority email support'].map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-indigo-500" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => void onUpgrade()}
              disabled={loading || remaining === 0}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Upgrade Now
            </button>
            <button
              type="button"
              onClick={onSeePlans}
              className="mx-auto block text-sm font-medium text-slate-500 underline underline-offset-4 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
            >
              See All Plans
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/70">
          <div className="mb-4 flex items-center justify-center gap-2 text-base font-semibold text-slate-950 dark:text-white">
            <Timer className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            Limited Time
          </div>
          <div className="flex items-center justify-center gap-3">
            {[countdown.minutes, countdown.seconds, countdown.milliseconds].map((unit, index) => (
              <React.Fragment key={`${unit}-${index}`}>
                {index > 0 && <span className="text-2xl font-bold text-slate-500 dark:text-zinc-400">:</span>}
                <span className="flex h-14 w-16 items-center justify-center rounded-lg border border-slate-200 bg-white font-mono text-2xl font-semibold text-slate-950 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-white">
                  {unit}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoYearlyOfferBanner;
